<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { useRoute } from "vue-router";

import {
  acceptSiteDiary,
  allocateDiaryStaff,
  applyDiaryFields,
  attachDiaryDrawing,
  attachDiaryPhoto,
  detachDiaryPhoto,
  fetchSiteDiaryCombined,
  draftDiaryReport,
  generateDiaryWorksSummary,
  getDiaryCarryForward,
  getDownloadUrl,
  getProject,
  reviewDiary,
  type DiaryReviewFinding,
  getDiaryRosterGang,
  getMe,
  getSiteDiary,
  getSiteDiaryHistory,
  listDrawings,
  listWorkPacks,
  pushEvents,
  removeDiaryDrawing,
  sha256OfFile,
  uploadFile,
  type DrawingRow,
  type NewEvent,
} from "@/api/client";
import { loadPdf, renderPdfPage } from "@/lib/pdf";
import DiaryBriefingLink from "@/components/DiaryBriefingLink.vue";
import DiaryMarkedUpDrawing from "@/components/DiaryMarkedUpDrawing.vue";
import SaveStatus from "@/components/SaveStatus.vue";
import FilePhotoGrid from "@/components/FilePhotoGrid.vue";
import LocationMap from "@/components/LocationMap.vue";
import PageLayout from "@/components/PageLayout.vue";
import SignaturePad from "@/components/SignaturePad.vue";
import { usePermissions } from "@/lib/permissions";

const route = useRoute();
const id = computed(() => route.params.id as string);
const queryClient = useQueryClient();

const { data, isPending, isError, error } = useQuery({
  queryKey: ["site-diary", id],
  queryFn: () => getSiteDiary(id.value),
});

// Audit trail (every change, from the event log).
const { data: history } = useQuery({
  queryKey: ["site-diary-history", id],
  queryFn: () => getSiteDiaryHistory(id.value),
});

function fmtDateTime(iso: string | null | undefined): string {
  return iso
    ? new Date(iso).toLocaleString("en-GB", {
        day: "numeric", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : "—";
}

// "Name · date" attribution, or null if there's nothing to show.
function attribution(
  name: string | null | undefined,
  at: string | null | undefined,
): string | null {
  if (!name && !at) return null;
  return [name || "—", at ? fmtDateTime(at) : null].filter(Boolean).join(" · ");
}

// Sign-off geo-tag → coords + a maps link.
const diarySignoffGeo = computed(() => {
  const g = data.value?.submitted_gps;
  if (!g || typeof g.lat !== "number" || typeof g.lng !== "number") return null;
  const acc = g.accuracy_m != null ? ` ±${Math.round(g.accuracy_m)}m` : "";
  return {
    label: `${g.lat.toFixed(5)}, ${g.lng.toFixed(5)}${acc}`,
    href: `https://www.google.com/maps?q=${g.lat},${g.lng}`,
  };
});

const fieldRows = computed(() =>
  Object.entries(data.value?.fields ?? {}).map(([key, value]) => {
    const arr = Array.isArray(value)
      ? (value as Array<Record<string, unknown>>)
      : null;
    // A grid value is an array of row objects (plant/personnel registers).
    const isGrid =
      !!arr && arr.length > 0 && typeof arr[0] === "object" && arr[0] !== null;
    return {
      key,
      // Humanise the field_path key (weather_conditions -> Weather conditions).
      label: key.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()),
      isGrid,
      gridColumns: isGrid
        ? Array.from(new Set(arr!.flatMap((r) => Object.keys(r ?? {}))))
        : [],
      gridRows: isGrid ? arr! : [],
      text: isGrid ? "" : String(value),
    };
  }),
);

const STATUS = {
  draft: { label: "Draft", class: "bg-neutral-100 text-neutral-600" },
  submitted: {
    label: "Submitted — awaiting approval",
    class: "bg-brand-accent/15 text-brand-accent",
  },
  approved: {
    label: "Approved — locked record",
    class: "bg-semantic-success/10 text-semantic-success",
  },
  rejected: {
    label: "Rejected — returned for correction",
    class: "bg-semantic-danger/10 text-semantic-danger",
  },
  reopened: { label: "Reopened", class: "bg-neutral-100 text-neutral-600" },
} as const;

const statusInfo = computed(
  () =>
    STATUS[(data.value?.status ?? "draft") as keyof typeof STATUS] ??
    STATUS.draft,
);

// Who the diary is routed to for approval (from the project's approval policy).
const APPROVER_LABELS: Record<string, string> = {
  cre: "CRE",
  project_manager: "Project Manager",
};
const approverRoleLabel = computed(() => {
  const r = data.value?.approver_role;
  return r ? (APPROVER_LABELS[r] ?? r) : "";
});

// Timestamp of the last successful save on this diary — surfaced in the header
// so the user gets confirmation that edits persisted.
const diarySavedAt = ref<number | null>(null);
function markSaved(): void {
  diarySavedAt.value = Date.now();
}

// --- approval actions ---
const busy = ref(false);
const actionError = ref<string | null>(null);
const showReject = ref(false);
const rejectReason = ref("");
// Approver digitally signs off before locking the record (brief §1.5).
const showApprove = ref(false);
const approveSignature = ref<string | null>(null);

// External clients get controlled, read-only access and may accept (§1.5).
// canApprove is the approver-role gate (matches the server + mobile) so the
// approve/reject controls aren't shown to users who can't actually approve.
const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe });
const isClient = computed(() => (me.value?.roles ?? []).includes("client"));
const { canApprove } = usePermissions();

// Combined submission record — diary + its linked check sheets as one ZIP.
const combinedBusy = ref(false);
async function downloadCombined(): Promise<void> {
  combinedBusy.value = true;
  actionError.value = null;
  try {
    const blob = await fetchSiteDiaryCombined(id.value);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `site-diary-${data.value?.diary_date ?? id.value}-record.zip`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    actionError.value = e instanceof Error ? e.message : String(e);
  } finally {
    combinedBusy.value = false;
  }
}
const showAccept = ref(false);
const acceptSignature = ref<string | null>(null);
async function acceptRecord(): Promise<void> {
  if (!acceptSignature.value) return;
  busy.value = true;
  actionError.value = null;
  try {
    await acceptSiteDiary(id.value, acceptSignature.value);
    await queryClient.invalidateQueries({ queryKey: ["site-diary", id] });
    await queryClient.invalidateQueries({ queryKey: ["site-diary-history", id] });
    showAccept.value = false;
    acceptSignature.value = null;
  } catch (e) {
    actionError.value = e instanceof Error ? e.message : String(e);
  } finally {
    busy.value = false;
  }
}

async function runAction(event: NewEvent): Promise<void> {
  busy.value = true;
  actionError.value = null;
  try {
    await pushEvents([event]);
    await queryClient.invalidateQueries({ queryKey: ["site-diary", id] });
    await queryClient.invalidateQueries({ queryKey: ["site-diary-history", id] });
    markSaved();
  } catch (e) {
    actionError.value = e instanceof Error ? e.message : String(e);
  } finally {
    busy.value = false;
  }
}

async function approve(): Promise<void> {
  if (!approveSignature.value) return;
  await runAction({
    aggregate_type: "site_diary",
    aggregate_id: id.value,
    event_type: "SiteDiaryApproved",
    payload: {
      approved_at: new Date().toISOString(),
      signature_data: approveSignature.value,
    },
  });
  showApprove.value = false;
  approveSignature.value = null;
}

async function reject(): Promise<void> {
  if (!rejectReason.value.trim()) return;
  await runAction({
    aggregate_type: "site_diary",
    aggregate_id: id.value,
    event_type: "SiteDiaryRejected",
    payload: {
      rejected_at: new Date().toISOString(),
      reason: rejectReason.value.trim(),
    },
  });
  showReject.value = false;
  rejectReason.value = "";
}

const reopen = (): Promise<void> =>
  runAction({
    aggregate_type: "site_diary",
    aggregate_id: id.value,
    event_type: "SiteDiaryReopened",
    payload: { reason: "Reopened for correction" },
  });

// --- marked-up drawings (visual evidence) ---
// Sourced from the project's work-pack drawings that have been marked up.
const projectId = computed(() => data.value?.project_id ?? null);
const { data: packs } = useQuery({
  queryKey: ["work-packs", projectId],
  queryFn: () => listWorkPacks(projectId.value),
  enabled: () => !!projectId.value,
});

const attachedFileIds = computed(
  () => new Set((data.value?.drawings ?? []).map((d) => d.file_id as string)),
);

const availableDrawings = computed(() =>
  (packs.value ?? []).flatMap((pack) =>
    pack.documents
      .filter((doc) => doc.markup && doc.file_id)
      .map((doc) => ({ pack, doc })),
  ).filter(({ doc }) => !attachedFileIds.value.has(doc.file_id as string)),
);

const showAttach = ref(false);

async function attachDrawing(pack: { id: string }, doc: {
  document_id: string; title: string; file_id: string | null;
  markup?: Record<string, unknown> | null;
}): Promise<void> {
  if (!doc.file_id) return;
  busy.value = true;
  try {
    await attachDiaryDrawing(id.value, {
      title: doc.title,
      file_id: doc.file_id,
      markup: doc.markup ?? null,
      source_work_pack_id: pack.id,
      source_document_id: doc.document_id,
    });
    await queryClient.invalidateQueries({ queryKey: ["site-diary", id] });
    showAttach.value = false;
  } finally {
    busy.value = false;
  }
}

async function removeDrawing(drawingId: string): Promise<void> {
  await removeDiaryDrawing(id.value, drawingId);
  await queryClient.invalidateQueries({ queryKey: ["site-diary", id] });
}

// --- attach a page from the Drawings register (§1.2) ---
// Drawings are created + marked up in the project Drawings register; the diary
// snapshots a page (image + its markup) as contemporaneous visual evidence.
const { data: registerDrawings } = useQuery({
  queryKey: ["drawings", projectId],
  queryFn: () => listDrawings(projectId.value),
  enabled: () => !!projectId.value,
});
const showRegisterPicker = ref(false);
const selectedRegisterDrawing = ref<DrawingRow | null>(null);

interface RegisterPage {
  pageNumber: number; // global page across the revision's uploads
  fileId: string; // the upload's file (image, or a multi-page PDF)
  mimeType: string;
  filePage: number; // 1-based page within that file
}
function registerPages(d: DrawingRow): RegisterPage[] {
  const rev =
    d.revisions.find((r) => r.revision_id === d.current_revision_id) ??
    d.revisions[d.revisions.length - 1];
  if (!rev) return [];
  const out: RegisterPage[] = [];
  let n = 0;
  for (const up of [...rev.uploads].sort((a, b) => a.position - b.position)) {
    for (let i = 1; i <= up.page_count; i += 1) {
      n += 1;
      out.push({
        pageNumber: n,
        fileId: up.file_id,
        mimeType: up.mime_type,
        filePage: i,
      });
    }
  }
  return out;
}

// Rasterise a PDF page to a PNG so the diary keeps a frozen image snapshot
// (the diary card renders an image + the markup overlay). Image pages are used
// as-is. Returns the file id to attach.
async function snapshotPageFile(page: RegisterPage): Promise<string> {
  if (page.mimeType !== "application/pdf") return page.fileId;
  const url = await getDownloadUrl(page.fileId);
  const doc = await loadPdf(url);
  try {
    const { canvas } = await renderPdfPage(doc, page.filePage, 1400);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/png"),
    );
    if (!blob) throw new Error("Could not render the PDF page.");
    const file = new File([blob], `drawing-p${page.pageNumber}.png`, {
      type: "image/png",
    });
    return uploadFile(file, "drawing_page");
  } finally {
    await doc.destroy().catch(() => {});
  }
}

async function attachFromRegister(
  d: DrawingRow,
  page: RegisterPage,
): Promise<void> {
  busy.value = true;
  try {
    const fileId = await snapshotPageFile(page);
    const shapes = (d.annotations?.[String(page.pageNumber)] as unknown[]) ?? [];
    await attachDiaryDrawing(id.value, {
      title: `${d.name} · p${page.pageNumber}`,
      file_id: fileId,
      markup: { shapes },
      source_drawing_id: d.id,
      page_number: page.pageNumber,
    });
    await queryClient.invalidateQueries({ queryKey: ["site-diary", id] });
    markSaved();
    showRegisterPicker.value = false;
    selectedRegisterDrawing.value = null;
  } finally {
    busy.value = false;
  }
}

// --- site location (postcode + zone + map) ---
const { data: project } = useQuery({
  queryKey: ["project", projectId],
  queryFn: () => getProject(projectId.value as string),
  enabled: () => !!projectId.value,
});

const sitePostcode = ref("");
const siteZone = ref("");
const locTouched = ref(false);
const locBusy = ref(false);
const locMsg = ref<string | null>(null);
const geo = ref<{ lat: number; lng: number } | null>(null);

// Seed from the saved diary fields, falling back to the project's site — but
// stop re-seeding once the user starts editing.
watch(
  [data, project],
  () => {
    if (locTouched.value) return;
    const f = data.value?.fields ?? {};
    sitePostcode.value =
      (f.site_postcode as string) || project.value?.postcode || "";
    siteZone.value = (f.site_zone as string) || "";
  },
  { immediate: true },
);

const mapLat = computed(() => geo.value?.lat ?? project.value?.latitude ?? null);
const mapLng = computed(() => geo.value?.lng ?? project.value?.longitude ?? null);

// Resolve a UK postcode to coordinates client-side (postcodes.io — free, no key,
// CORS-enabled). Keeps the last known centre on failure.
async function geocodeSite(): Promise<void> {
  const cleaned = sitePostcode.value.trim().replace(/\s+/g, "");
  if (!cleaned) return;
  try {
    const r = await fetch(
      `https://api.postcodes.io/postcodes/${encodeURIComponent(cleaned)}`,
    );
    if (!r.ok) return;
    const res = (await r.json())?.result;
    if (res?.latitude != null && res?.longitude != null) {
      geo.value = { lat: res.latitude, lng: res.longitude };
    }
  } catch {
    /* keep last known centre */
  }
}
watch(sitePostcode, () => void geocodeSite(), { immediate: true });

async function saveSiteLocation(): Promise<void> {
  locBusy.value = true;
  locMsg.value = null;
  try {
    await applyDiaryFields(id.value, [
      { field_path: "site_postcode", value: sitePostcode.value.trim() },
      { field_path: "site_zone", value: siteZone.value.trim() },
    ]);
    await queryClient.invalidateQueries({ queryKey: ["site-diary", id] });
    await queryClient.invalidateQueries({ queryKey: ["site-diary-history", id] });
    markSaved();
    locMsg.value = "Site location saved.";
  } catch (e) {
    locMsg.value = e instanceof Error ? e.message : String(e);
  } finally {
    locBusy.value = false;
  }
}

// --- site photos: upload + attach on the web (parity with the mobile app) ---
const photoBusy = ref(false);
const photoError = ref<string | null>(null);
const photoFileIds = computed(() =>
  (data.value?.photos ?? [])
    .map((p) => (p as Record<string, unknown>).file_id as string)
    .filter(Boolean),
);

async function onPhotoPick(e: Event): Promise<void> {
  const input = e.target as HTMLInputElement;
  const files = Array.from(input.files ?? []);
  if (!files.length) return;
  photoBusy.value = true;
  photoError.value = null;
  try {
    for (const file of files) {
      const sha256 = await sha256OfFile(file);
      const fileId = await uploadFile(file, "site_diary_photo");
      await attachDiaryPhoto(id.value, {
        field_path: "site_photos",
        file_id: fileId,
        sha256,
        mime_type: file.type || "application/octet-stream",
        size_bytes: file.size,
      });
    }
    await queryClient.invalidateQueries({ queryKey: ["site-diary", id] });
    markSaved();
  } catch (err) {
    photoError.value = err instanceof Error ? err.message : String(err);
  } finally {
    photoBusy.value = false;
    input.value = "";
  }
}

async function removePhoto(fileId: string): Promise<void> {
  const p = (data.value?.photos ?? []).find(
    (x) => (x as Record<string, unknown>).file_id === fileId,
  ) as Record<string, unknown> | undefined;
  await detachDiaryPhoto(id.value, {
    field_path: (p?.field_path as string) || "site_photos",
    file_id: fileId,
  });
  await queryClient.invalidateQueries({ queryKey: ["site-diary", id] });
}

// --- pre-populate the gang from the Roster (brief §1.2) ---
const gangBusy = ref(false);
const gangMsg = ref<string | null>(null);
async function pullGangFromRoster(): Promise<void> {
  gangBusy.value = true;
  gangMsg.value = null;
  try {
    const gang = await getDiaryRosterGang(id.value);
    if (gang.length === 0) {
      gangMsg.value = "No one rostered for this date/project.";
      return;
    }
    await allocateDiaryStaff(
      id.value,
      gang.map((g) => ({ person_id: g.person_id, name: g.name, role: g.role })),
    );
    await queryClient.invalidateQueries({ queryKey: ["site-diary", id] });
    gangMsg.value = `Pulled ${gang.length} from the roster.`;
  } catch (e) {
    gangMsg.value = e instanceof Error ? e.message : String(e);
  } finally {
    gangBusy.value = false;
  }
}

// --- Carry-forward (Phase 1): seed the diary from yesterday + the roster ---
// Office pre-population so the supervisor opens a mostly-filled form. Fill-empty
// only — never clobbers anything already captured, never invents content.
const carryBusy = ref(false);
const carryMsg = ref<string | null>(null);

function isBlankValue(v: unknown): boolean {
  return (
    v == null ||
    (typeof v === "string" && !v.trim()) ||
    (Array.isArray(v) && v.length === 0)
  );
}

async function copyFromYesterday(): Promise<void> {
  if (!data.value) return;
  carryBusy.value = true;
  carryMsg.value = null;
  try {
    const proposal = await getDiaryCarryForward({
      projectId: data.value.project_id,
      date: data.value.diary_date,
      projectCode: data.value.project_code,
    });
    const current = data.value.fields ?? {};
    const toApply = proposal.fields.filter((f) =>
      isBlankValue(current[f.field_path]),
    );
    if (toApply.length === 0) {
      carryMsg.value = proposal.prior_diary_date || proposal.has_roster
        ? "Nothing to copy — those fields are already filled."
        : "No earlier diary or roster to copy from.";
      return;
    }
    await applyDiaryFields(
      id.value,
      toApply.map((f) => ({ field_path: f.field_path, value: f.value })),
    );
    await queryClient.invalidateQueries({ queryKey: ["site-diary", id] });
    await queryClient.invalidateQueries({ queryKey: ["site-diary-history", id] });
    carryMsg.value = `Filled ${toApply.length} field(s): ${toApply
      .map((f) => f.label)
      .join(", ")}.`;
  } catch (e) {
    carryMsg.value = e instanceof Error ? e.message : String(e);
  } finally {
    carryBusy.value = false;
  }
}

// --- AI draft work report (Phase 2) ---
// Drafts the diary's required `actual_work` narrative from the day's records
// (check sheets, defects/close calls, notes, planned work). Returned for review
// in an editable box — saving emits a SiteDiaryFieldUpdated, never silently.
const draftBusy = ref(false);
const draftMsg = ref<string | null>(null);
const draftText = ref<string | null>(null);

async function draftReport(): Promise<void> {
  draftBusy.value = true;
  draftMsg.value = null;
  try {
    const r = await draftDiaryReport(id.value);
    draftText.value = r.draft;
    const bits = [
      `${r.check_sheet_count} check sheet(s)`,
      `${r.entry_count} note(s)`,
      `${r.defect_count} defect(s)`,
      `${r.observation_count} close call(s)`,
    ];
    draftMsg.value = `Drafted from ${bits.join(", ")}. Review and edit before saving.`;
  } catch (e) {
    draftMsg.value = e instanceof Error ? e.message : String(e);
  } finally {
    draftBusy.value = false;
  }
}

async function saveDraft(): Promise<void> {
  if (!draftText.value?.trim()) return;
  draftBusy.value = true;
  try {
    await applyDiaryFields(id.value, [
      { field_path: "actual_work", value: draftText.value.trim() },
    ]);
    await queryClient.invalidateQueries({ queryKey: ["site-diary", id] });
    await queryClient.invalidateQueries({ queryKey: ["site-diary-history", id] });
    markSaved();
    draftText.value = null;
    draftMsg.value = "Saved to the work report.";
  } catch (e) {
    draftMsg.value = e instanceof Error ? e.message : String(e);
  } finally {
    draftBusy.value = false;
  }
}

// --- Pre-sign-off review (Phase 4) ---
// Advisory sense check: flags inconsistencies, missing-but-implied items and
// gaps before sign-off. Never blocks — just surfaces what's worth a look.
const reviewBusy = ref(false);
const reviewMsg = ref<string | null>(null);
const reviewFindings = ref<DiaryReviewFinding[] | null>(null);

async function checkDiary(): Promise<void> {
  reviewBusy.value = true;
  reviewMsg.value = null;
  try {
    const r = await reviewDiary(id.value);
    reviewFindings.value = r.findings;
    reviewMsg.value = r.ok
      ? "Diary looks complete and consistent — nothing flagged."
      : `${r.findings.length} thing(s) to check before sign-off.`;
  } catch (e) {
    reviewMsg.value = e instanceof Error ? e.message : String(e);
  } finally {
    reviewBusy.value = false;
  }
}

// --- AI works-completed summary (brief §1.4) ---
// Derived from the day's signed-off check sheets, grouped by location + status,
// so the supervisor doesn't re-type a narrative that duplicates the QA records.
const worksBusy = ref(false);
const worksMsg = ref<string | null>(null);
async function generateWorksSummary(): Promise<void> {
  worksBusy.value = true;
  worksMsg.value = null;
  try {
    const r = await generateDiaryWorksSummary(id.value);
    await queryClient.invalidateQueries({ queryKey: ["site-diary", id] });
    await queryClient.invalidateQueries({ queryKey: ["site-diary-history", id] });
    worksMsg.value = `Summarised ${r.source_sheet_count} signed-off check sheet(s).`;
  } catch (e) {
    worksMsg.value = e instanceof Error ? e.message : String(e);
  } finally {
    worksBusy.value = false;
  }
}

// --- freeform diary entries (brief §1.3 Option B) ---
const newEntry = ref("");
const newEntryLocation = ref("");

async function addEntry(): Promise<void> {
  if (!newEntry.value.trim()) return;
  await runAction({
    aggregate_type: "site_diary",
    aggregate_id: id.value,
    event_type: "SiteDiaryEntryAdded",
    payload: {
      entry_id: crypto.randomUUID(),
      mode: "freeform",
      description: newEntry.value.trim(),
      // The zone/area the work was done in (brief: exact zones/areas).
      ...(newEntryLocation.value.trim()
        ? { location_ref: newEntryLocation.value.trim() }
        : {}),
    },
  });
  newEntry.value = "";
  newEntryLocation.value = "";
}

const removeEntry = (entryId: string): Promise<void> =>
  runAction({
    aggregate_type: "site_diary",
    aggregate_id: id.value,
    event_type: "SiteDiaryEntryRemoved",
    payload: { entry_id: entryId },
  });
</script>

<template>
  <PageLayout>
    <RouterLink
      :to="{ name: 'site-diaries', params: { projectId: $route.params.projectId } }"
      class="text-sm text-neutral-500 hover:text-neutral-900"
    >
      ← Site diaries
    </RouterLink>

    <div
      v-if="isPending"
      class="text-neutral-500 mt-4"
    >
      Loading…
    </div>
    <div
      v-else-if="isError"
      class="bg-semantic-danger/10 text-semantic-danger rounded-md p-4 mt-4"
    >
      Failed to load: {{ error?.message }}
    </div>
    <div
      v-else-if="data"
      class="mt-4 grid gap-6"
    >
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <div class="flex items-start justify-between gap-4">
          <h1 class="text-xl font-semibold">
            {{ data.project_name }}
          </h1>
          <SaveStatus
            :saving="busy"
            :saved-at="diarySavedAt"
          />
        </div>
        <dl
          class="mt-4 grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm"
        >
          <dt class="text-neutral-500">
            Project code
          </dt>
          <dd>{{ data.project_code ?? "—" }}</dd>
          <dt class="text-neutral-500">
            Client
          </dt>
          <dd>{{ data.client_name ?? "—" }}</dd>
          <dt class="text-neutral-500">
            Date
          </dt>
          <dd>{{ data.diary_date }}</dd>
          <dt class="text-neutral-500">
            Rail week
          </dt>
          <dd>{{ data.rail_week ?? "—" }}</dd>
          <dt class="text-neutral-500">
            Shift
          </dt>
          <dd>{{ data.shift_start ?? "—" }} – {{ data.shift_end ?? "—" }}</dd>

          <template v-if="attribution(data.created_by_name, data.created_at)">
            <dt class="text-neutral-500">
              Created by
            </dt>
            <dd>{{ attribution(data.created_by_name, data.created_at) }}</dd>
          </template>
          <template
            v-if="attribution(data.last_updated_by_name, data.last_updated_at)"
          >
            <dt class="text-neutral-500">
              Last edited by
            </dt>
            <dd>{{ attribution(data.last_updated_by_name, data.last_updated_at) }}</dd>
          </template>
          <template v-if="attribution(data.submitted_by_name, data.submitted_at)">
            <dt class="text-neutral-500">
              Submitted by
            </dt>
            <dd>{{ attribution(data.submitted_by_name, data.submitted_at) }}</dd>
          </template>
          <template v-if="data.submitted_signature">
            <dt class="text-neutral-500">
              Sign-off signature
            </dt>
            <dd>
              <a
                :href="data.submitted_signature"
                target="_blank"
                rel="noopener"
                title="Open full size"
              >
                <img
                  :src="data.submitted_signature"
                  alt="sign-off signature"
                  class="h-10 w-28 rounded border border-neutral-200 bg-white object-contain"
                >
              </a>
            </dd>
          </template>
          <template v-if="diarySignoffGeo">
            <dt class="text-neutral-500">
              Sign-off location
            </dt>
            <dd>
              <a
                :href="diarySignoffGeo.href"
                target="_blank"
                rel="noopener"
                class="text-brand-primary hover:underline"
              >{{ diarySignoffGeo.label }}</a>
            </dd>
          </template>
          <template v-if="attribution(data.approved_by_name, data.approved_at)">
            <dt class="text-neutral-500">
              Approved by
            </dt>
            <dd>{{ attribution(data.approved_by_name, data.approved_at) }}</dd>
          </template>
          <template v-if="data.approved_signature">
            <dt class="text-neutral-500">
              Approver signature
            </dt>
            <dd>
              <a
                :href="data.approved_signature"
                target="_blank"
                rel="noopener"
                title="Open full size"
              >
                <img
                  :src="data.approved_signature"
                  alt="approver signature"
                  class="h-10 w-28 rounded border border-neutral-200 bg-white object-contain"
                >
              </a>
            </dd>
          </template>
          <template v-if="attribution(data.client_accepted_by_name, data.client_accepted_at)">
            <dt class="text-neutral-500">
              Accepted by client
            </dt>
            <dd>{{ attribution(data.client_accepted_by_name, data.client_accepted_at) }}</dd>
          </template>
        </dl>
      </section>

      <!-- Briefing & sign-on for this shift -->
      <DiaryBriefingLink :diary="data" />

      <!-- Approval -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <div class="mb-3 flex items-center justify-between gap-3">
          <h2 class="font-semibold">
            Approval
          </h2>
          <button
            type="button"
            class="shrink-0 rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
            :disabled="combinedBusy"
            title="Download the diary plus all linked check sheets as one ZIP"
            @click="downloadCombined"
          >
            {{ combinedBusy ? "Preparing…" : "Combined record (ZIP)" }}
          </button>
        </div>
        <span
          class="inline-block rounded-full px-3 py-1 text-xs font-semibold"
          :class="statusInfo.class"
        >
          {{ statusInfo.label }}
        </span>
        <p
          v-if="data.submitted_by_name"
          class="mt-2 text-sm text-neutral-700"
        >
          Submitted by <span class="font-medium">{{ data.submitted_by_name }}</span><span
            v-if="data.submitted_at"
            class="text-neutral-500"
          > · {{ fmtDateTime(data.submitted_at) }}</span>
        </p>
        <p
          v-if="data.status === 'submitted' && approverRoleLabel"
          class="mt-1 text-sm text-neutral-600"
        >
          Awaiting approval from <span class="font-medium">{{ approverRoleLabel }}</span><span
            v-if="data.approver_emails?.length"
            class="text-neutral-500"
          > · notifies {{ data.approver_emails.join(", ") }}</span>
        </p>
        <p
          v-if="data.rejection_reason"
          class="mt-3 text-sm text-semantic-danger"
        >
          Rejection reason: {{ data.rejection_reason }}
        </p>

        <div
          v-if="canApprove"
          class="mt-4 flex flex-wrap gap-3"
        >
          <template v-if="data.status === 'submitted'">
            <button
              type="button"
              class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on transition hover:opacity-90 disabled:opacity-50"
              :disabled="busy"
              @click="showApprove = !showApprove; showReject = false"
            >
              Approve &amp; lock
            </button>
            <button
              type="button"
              class="rounded-md border border-neutral-300 px-4 py-2 text-sm font-medium hover:bg-neutral-50"
              @click="showReject = !showReject; showApprove = false"
            >
              Reject
            </button>
          </template>
          <button
            v-else-if="['approved', 'rejected'].includes(data.status)"
            type="button"
            class="rounded-md border border-neutral-300 px-4 py-2 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
            :disabled="busy"
            @click="reopen"
          >
            Reopen
          </button>
          <span
            v-else
            class="text-sm text-neutral-500"
          >
            No approval action — the diary has not been submitted yet.
          </span>
        </div>

        <div
          v-if="showApprove && canApprove"
          class="mt-3 grid gap-2"
        >
          <p class="text-sm text-neutral-600">
            Sign below to approve and lock this record.
          </p>
          <SignaturePad v-model="approveSignature" />
          <div class="flex gap-2">
            <button
              type="button"
              class="rounded-md bg-brand-primary px-3 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
              :disabled="busy || !approveSignature"
              @click="approve"
            >
              Approve &amp; lock
            </button>
            <button
              type="button"
              class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
              @click="showApprove = false"
            >
              Cancel
            </button>
          </div>
        </div>

        <div
          v-if="showReject && canApprove"
          class="mt-3 grid gap-2"
        >
          <textarea
            v-model="rejectReason"
            rows="2"
            placeholder="Reason for rejection"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
          />
          <div class="flex gap-2">
            <button
              type="button"
              class="rounded-md bg-brand-primary px-3 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
              :disabled="busy || !rejectReason.trim()"
              @click="reject"
            >
              Send rejection
            </button>
            <button
              type="button"
              class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
              @click="showReject = false"
            >
              Cancel
            </button>
          </div>
        </div>

        <!-- Client controlled access: view + accept approved records (§1.5) -->
        <div
          v-if="isClient"
          class="mt-4"
        >
          <p
            v-if="data.client_accepted_at"
            class="text-sm text-semantic-success"
          >
            ✓ You accepted this record on
            {{ fmtDateTime(data.client_accepted_at) }}.
          </p>
          <template v-else-if="data.status === 'approved'">
            <button
              v-if="!showAccept"
              type="button"
              class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on transition hover:opacity-90"
              @click="showAccept = true"
            >
              Accept record
            </button>
            <div
              v-else
              class="grid gap-2"
            >
              <p class="text-sm text-neutral-600">
                Sign below to accept this approved record.
              </p>
              <SignaturePad v-model="acceptSignature" />
              <div class="flex gap-2">
                <button
                  type="button"
                  class="rounded-md bg-brand-primary px-3 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
                  :disabled="busy || !acceptSignature"
                  @click="acceptRecord"
                >
                  Accept
                </button>
                <button
                  type="button"
                  class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
                  @click="showAccept = false"
                >
                  Cancel
                </button>
              </div>
            </div>
          </template>
          <p
            v-else
            class="text-sm text-neutral-500"
          >
            This record is not yet approved.
          </p>
        </div>

        <p
          v-if="actionError"
          class="mt-2 text-sm text-semantic-danger"
        >
          {{ actionError }}
        </p>
      </section>

      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <div class="mb-3 flex items-start justify-between gap-4">
          <h2 class="font-semibold">
            Staff on shift ({{ data.staff.length }})
          </h2>
          <button
            type="button"
            class="shrink-0 rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
            :disabled="gangBusy"
            @click="pullGangFromRoster"
          >
            {{ gangBusy ? "Pulling…" : "Pull gang from roster" }}
          </button>
        </div>
        <p
          v-if="gangMsg"
          class="mb-2 text-sm text-neutral-500"
        >
          {{ gangMsg }}
        </p>
        <ul
          v-if="data.staff.length"
          class="text-sm grid gap-1"
        >
          <li
            v-for="(s, i) in data.staff"
            :key="i"
          >
            {{ (s as Record<string, unknown>).name }}
            <span class="text-neutral-400">
              · {{ (s as Record<string, unknown>).role ?? "—" }}
            </span>
          </li>
        </ul>
        <p
          v-else
          class="text-sm text-neutral-500"
        >
          No staff recorded.
        </p>
      </section>

      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <div class="mb-3 flex items-start justify-between gap-4">
          <h2 class="font-semibold">
            Report
          </h2>
          <div
            v-if="!isClient && ['draft', 'reopened'].includes(data.status)"
            class="flex shrink-0 gap-2"
          >
            <button
              type="button"
              class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
              :disabled="carryBusy"
              title="Pre-fill empty fields from yesterday's diary and the roster"
              @click="copyFromYesterday"
            >
              {{ carryBusy ? "Copying…" : "Copy from yesterday" }}
            </button>
            <button
              type="button"
              class="rounded-md border border-brand-primary/40 bg-brand-primary/5 px-3 py-1.5 text-sm font-medium text-brand-primary hover:bg-brand-primary/10 disabled:opacity-50"
              :disabled="draftBusy"
              title="Draft the work report from today's check sheets, defects, notes and plan"
              @click="draftReport"
            >
              {{ draftBusy ? "Drafting…" : "✨ Draft work report" }}
            </button>
            <button
              type="button"
              class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
              :disabled="reviewBusy"
              title="Check the diary for inconsistencies and gaps before sign-off"
              @click="checkDiary"
            >
              {{ reviewBusy ? "Checking…" : "Check diary" }}
            </button>
          </div>
        </div>
        <p
          v-if="carryMsg"
          class="mb-2 text-sm text-neutral-500"
        >
          {{ carryMsg }}
        </p>
        <!-- AI draft preview — editable, never saved without confirmation. -->
        <div
          v-if="draftText !== null"
          class="mb-3 rounded-lg border border-brand-primary/30 bg-brand-primary/5 p-3"
        >
          <p class="mb-2 text-xs font-medium text-brand-primary">
            ✨ Suggested work report — review and edit, then save.
          </p>
          <textarea
            v-model="draftText"
            rows="6"
            class="w-full rounded-md border border-neutral-300 p-2 text-sm"
          />
          <div class="mt-2 flex items-center gap-2">
            <button
              type="button"
              class="rounded-md bg-brand-primary px-3 py-1.5 text-sm font-medium text-neutral-0 hover:opacity-90 disabled:opacity-50"
              :disabled="draftBusy || !draftText.trim()"
              @click="saveDraft"
            >
              Save as work report
            </button>
            <button
              type="button"
              class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
              @click="draftText = null"
            >
              Discard
            </button>
          </div>
        </div>
        <p
          v-if="draftMsg"
          class="mb-2 text-sm text-neutral-500"
        >
          {{ draftMsg }}
        </p>
        <!-- Pre-sign-off review findings (advisory). -->
        <div
          v-if="reviewFindings !== null"
          class="mb-3"
        >
          <p
            v-if="reviewMsg"
            class="mb-2 text-sm"
            :class="reviewFindings.length ? 'text-neutral-700' : 'text-semantic-success'"
          >
            {{ reviewMsg }}
          </p>
          <ul
            v-if="reviewFindings.length"
            class="grid gap-1.5"
          >
            <li
              v-for="(f, i) in reviewFindings"
              :key="i"
              class="flex items-start gap-2 rounded-md border px-3 py-2 text-sm"
              :class="f.severity === 'warning'
                ? 'border-semantic-warning/40 bg-semantic-warning/5'
                : 'border-neutral-200 bg-neutral-50'"
            >
              <span class="shrink-0">{{ f.severity === "warning" ? "⚠️" : "💡" }}</span>
              <span>{{ f.message }}</span>
            </li>
          </ul>
        </div>
        <dl
          v-if="fieldRows.length"
          class="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm"
        >
          <template
            v-for="row in fieldRows"
            :key="row.key"
          >
            <dt class="text-neutral-500">
              {{ row.label }}
            </dt>
            <dd class="whitespace-pre-wrap">
              <table
                v-if="row.isGrid"
                class="w-full border border-neutral-200 text-xs"
              >
                <thead>
                  <tr>
                    <th
                      v-for="c in row.gridColumns"
                      :key="c"
                      class="border-b border-neutral-200 px-2 py-1 text-left font-medium capitalize text-neutral-500"
                    >
                      {{ c }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(r, i) in row.gridRows"
                    :key="i"
                    class="border-b border-neutral-100 last:border-0"
                  >
                    <td
                      v-for="c in row.gridColumns"
                      :key="c"
                      class="px-2 py-1"
                    >
                      {{ r[c] ?? "" }}
                    </td>
                  </tr>
                </tbody>
              </table>
              <template v-else>
                {{ row.text }}
              </template>
            </dd>
          </template>
        </dl>
        <p
          v-else
          class="text-sm text-neutral-500"
        >
          No on-site fields completed yet.
        </p>
      </section>

      <!-- Site location (postcode prefilled from the project + map) -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <h2 class="mb-1 font-semibold">
          Site location
        </h2>
        <p class="mb-3 text-xs text-neutral-500">
          Prefilled from the project's site — adjust the postcode or zone for
          today's works. The map updates from the postcode.
        </p>
        <div class="grid gap-3 sm:grid-cols-2">
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Postcode</span>
            <input
              v-model="sitePostcode"
              type="text"
              :disabled="data.status === 'approved'"
              placeholder="e.g. PH1 2SJ"
              class="rounded-md border border-neutral-300 px-3 py-2 text-sm disabled:bg-neutral-50"
              @input="locTouched = true"
            >
          </label>
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Location / zone (free text)</span>
            <input
              v-model="siteZone"
              type="text"
              :disabled="data.status === 'approved'"
              placeholder="e.g. Loc 47, Up Main"
              class="rounded-md border border-neutral-300 px-3 py-2 text-sm disabled:bg-neutral-50"
              @input="locTouched = true"
            >
          </label>
        </div>
        <div class="mt-3">
          <LocationMap
            :lat="mapLat"
            :lng="mapLng"
            :label="sitePostcode || project?.location_name || 'Site'"
          />
        </div>
        <div
          v-if="data.status !== 'approved'"
          class="mt-3 flex items-center gap-3"
        >
          <button
            type="button"
            class="rounded-md bg-brand-primary px-3 py-2 text-sm font-semibold text-brand-primary-on transition hover:opacity-90 disabled:opacity-50"
            :disabled="locBusy"
            @click="saveSiteLocation"
          >
            {{ locBusy ? "Saving…" : "Save site location" }}
          </button>
          <span
            v-if="locMsg"
            class="text-sm text-neutral-500"
          >{{ locMsg }}</span>
        </div>
      </section>

      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <h2 class="mb-2 font-semibold">
          Diary entries ({{ data.entries.length }})
        </h2>

        <!-- Mode C: QA-verified activities, auto-imported from signed-off
             check sheets for this project + day (computed live). -->
        <ul
          v-if="(data.qa_entries ?? []).length"
          class="mb-3 grid gap-2 text-sm"
        >
          <li
            v-for="(q, i) in data.qa_entries"
            :key="'qa' + i"
            class="flex items-start gap-2"
          >
            <span class="shrink-0 rounded bg-semantic-success/10 px-1.5 py-0.5 text-xs font-medium text-semantic-success">
              ✓ QA Verified
            </span>
            <span>
              {{ (q as Record<string, unknown>).activity }}
              <span class="text-neutral-400">
                · {{ (q as Record<string, unknown>).reference }}
                · {{ String((q as Record<string, unknown>).status).replace("_", " ") }}
              </span>
              <RouterLink
                :to="{ name: 'check-sheet-detail', params: { projectId: data.project_id, id: String((q as Record<string, unknown>).check_sheet_id) } }"
                class="ml-1 text-brand-primary hover:underline"
              >view</RouterLink>
            </span>
          </li>
        </ul>

        <ul
          v-if="data.entries.length"
          class="grid gap-2 text-sm"
        >
          <li
            v-for="(e, i) in data.entries"
            :key="i"
            class="flex items-start justify-between gap-3"
          >
            <span>
              <span
                class="mr-2 rounded bg-neutral-100 px-1.5 py-0.5 text-xs text-neutral-500"
              >
                {{ String((e as Record<string, unknown>).mode ?? "freeform")
                  .replace("_", " ") }}
              </span>
              <span
                v-if="(e as Record<string, unknown>).location_ref"
                class="mr-2 rounded bg-brand-primary/10 px-1.5 py-0.5 text-xs font-medium text-brand-primary"
              >📍 {{ (e as Record<string, unknown>).location_ref }}</span>
              {{ (e as Record<string, unknown>).description }}
            </span>
            <button
              type="button"
              class="shrink-0 text-neutral-400 hover:text-semantic-danger"
              title="Remove entry"
              @click="removeEntry(
                String((e as Record<string, unknown>).entry_id))"
            >
              ✕
            </button>
          </li>
        </ul>
        <p
          v-else
          class="text-sm text-neutral-500"
        >
          No entries.
        </p>

        <div class="mt-4 grid gap-2">
          <textarea
            v-model="newEntry"
            rows="2"
            placeholder="Add a freeform note — unplanned works, site events…"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
          />
          <input
            v-model="newEntryLocation"
            type="text"
            placeholder="Location / zone (optional, e.g. Loc 47, Up Main)"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
          <div>
            <button
              type="button"
              class="rounded-md bg-brand-primary px-3 py-2 text-sm font-semibold text-brand-primary-on transition hover:opacity-90 disabled:opacity-50"
              :disabled="busy || !newEntry.trim()"
              @click="addEntry"
            >
              Add entry
            </button>
          </div>
        </div>

        <!-- Site photos: attach on the web (parity with the mobile app) -->
        <div class="mt-6 border-t border-neutral-100 pt-4">
          <div class="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h3 class="text-sm font-semibold text-neutral-900">
              Photos ({{ photoFileIds.length }})
            </h3>
            <label
              v-if="data.status !== 'approved'"
              class="cursor-pointer rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
              :class="photoBusy ? 'pointer-events-none opacity-50' : ''"
            >
              {{ photoBusy ? "Uploading…" : "+ Add photos" }}
              <input
                type="file"
                accept="image/*"
                multiple
                class="hidden"
                @change="onPhotoPick"
              >
            </label>
          </div>
          <FilePhotoGrid
            v-if="photoFileIds.length"
            :file-ids="photoFileIds"
            :removable="data.status !== 'approved'"
            @remove="removePhoto"
          />
          <p
            v-else
            class="text-sm text-neutral-500"
          >
            No photos yet.
            <span v-if="data.status !== 'approved'">
              Use “+ Add photos”, or capture them on the mobile app.
            </span>
          </p>
          <p
            v-if="photoError"
            class="mt-2 text-sm text-semantic-danger"
          >
            {{ photoError }}
          </p>
        </div>
      </section>

      <!-- Works summary (AI) — derived from the day's signed-off check sheets -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <div class="mb-3 flex items-start justify-between gap-4">
          <div>
            <h2 class="font-semibold">
              Works summary (AI)
            </h2>
            <p class="mt-0.5 text-xs text-neutral-500">
              Works completed today, grouped by location — generated from the
              signed-off check sheets.
            </p>
          </div>
          <button
            type="button"
            class="shrink-0 rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
            :disabled="worksBusy"
            @click="generateWorksSummary"
          >
            {{ worksBusy
              ? "Generating…"
              : data.works_summary
                ? "Regenerate"
                : "Generate" }}
          </button>
        </div>
        <p
          v-if="data.works_summary"
          class="whitespace-pre-wrap text-sm text-neutral-900"
        >
          {{ data.works_summary }}
        </p>
        <p
          v-else
          class="text-sm text-neutral-500"
        >
          No summary yet. Generate one once check sheets have been signed off
          for this date.
        </p>
        <p
          v-if="data.works_summary_at"
          class="mt-2 text-xs text-neutral-400"
        >
          Generated {{ fmtDateTime(data.works_summary_at) }}
          <span v-if="data.works_summary_model">· {{ data.works_summary_model }}</span>
          <span v-else>· deterministic (no model configured)</span>
        </p>
        <p
          v-if="worksMsg"
          class="mt-2 text-sm text-neutral-500"
        >
          {{ worksMsg }}
        </p>
      </section>

      <!-- Marked-up drawings (visual evidence) -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 class="font-semibold">
            Marked-up drawings ({{ data.drawings.length }})
          </h2>
          <div
            v-if="data.status !== 'approved'"
            class="flex flex-wrap items-center gap-2"
          >
            <button
              v-if="(registerDrawings ?? []).length"
              type="button"
              class="rounded-md bg-brand-primary px-3 py-1.5 text-sm font-medium text-brand-primary-on transition hover:opacity-90"
              @click="showRegisterPicker = !showRegisterPicker; showAttach = false; selectedRegisterDrawing = null"
            >
              {{ showRegisterPicker ? "Cancel" : "+ From Drawings" }}
            </button>
            <button
              v-if="availableDrawings.length"
              type="button"
              class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
              @click="showAttach = !showAttach; showRegisterPicker = false"
            >
              {{ showAttach ? "Cancel" : "Attach work-pack drawing" }}
            </button>
          </div>
        </div>

        <!-- Attach a marked-up page from the Drawings register -->
        <div
          v-if="showRegisterPicker"
          class="mb-4 rounded-lg bg-neutral-50 p-3 text-sm"
        >
          <template v-if="!selectedRegisterDrawing">
            <p class="mb-1 text-xs text-neutral-500">
              Choose a drawing:
            </p>
            <ul class="grid gap-1">
              <li
                v-for="dr in (registerDrawings ?? [])"
                :key="dr.id"
                class="flex items-center justify-between gap-3"
              >
                <span>
                  <span class="font-medium text-neutral-900">{{ dr.name }}</span>
                  <span class="text-neutral-400"> · {{ dr.page_count }} page(s)</span>
                </span>
                <button
                  type="button"
                  class="shrink-0 text-brand-primary hover:underline"
                  @click="selectedRegisterDrawing = dr"
                >
                  Choose
                </button>
              </li>
            </ul>
          </template>
          <template v-else>
            <div class="mb-2 flex items-center gap-2">
              <button
                type="button"
                class="text-neutral-400 hover:text-neutral-900"
                @click="selectedRegisterDrawing = null"
              >
                ‹ Back
              </button>
              <span class="font-medium text-neutral-900">
                {{ selectedRegisterDrawing!.name }}
              </span>
            </div>
            <div
              v-if="registerPages(selectedRegisterDrawing!).length"
              class="flex flex-wrap gap-2"
            >
              <button
                v-for="pg in registerPages(selectedRegisterDrawing!)"
                :key="pg.pageNumber"
                type="button"
                class="rounded-md border border-neutral-300 bg-neutral-0 px-3 py-1.5 hover:bg-neutral-50 disabled:opacity-50"
                :disabled="busy"
                @click="attachFromRegister(selectedRegisterDrawing!, pg)"
              >
                Attach page {{ pg.pageNumber }}
              </button>
            </div>
            <p
              v-else
              class="text-neutral-400"
            >
              This drawing has no pages yet.
            </p>
            <p
              v-if="busy"
              class="mt-2 text-xs text-neutral-500"
            >
              Preparing the page snapshot…
            </p>
          </template>
        </div>

        <!-- Picker: marked-up drawings from the project's work packs -->
        <ul
          v-if="showAttach"
          class="mb-4 grid gap-1 rounded-lg bg-neutral-50 p-3 text-sm"
        >
          <li
            v-for="{ pack, doc } in availableDrawings"
            :key="doc.document_id"
            class="flex items-center justify-between gap-3"
          >
            <span>
              <span class="font-medium text-neutral-900">{{ doc.title }}</span>
              <span class="text-neutral-400"> · {{ pack.title }}</span>
            </span>
            <button
              type="button"
              class="shrink-0 text-brand-primary hover:underline disabled:opacity-50"
              :disabled="busy"
              @click="attachDrawing(pack, doc)"
            >
              Attach
            </button>
          </li>
        </ul>

        <div
          v-if="data.drawings.length"
          class="grid gap-4 sm:grid-cols-2"
        >
          <DiaryMarkedUpDrawing
            v-for="d in data.drawings"
            :key="String(d.drawing_id)"
            :drawing="d"
          >
            <template #action>
              <button
                v-if="data.status !== 'approved'"
                type="button"
                class="text-neutral-400 hover:text-semantic-danger"
                title="Remove drawing"
                @click="removeDrawing(String(d.drawing_id))"
              >
                ✕
              </button>
            </template>
          </DiaryMarkedUpDrawing>
        </div>
        <p
          v-else
          class="text-sm text-neutral-500"
        >
          No drawings attached.
          <span
            v-if="(registerDrawings ?? []).length"
            class="text-neutral-400"
          >
            (Use “+ From Drawings” to attach a marked-up page from the register.)
          </span>
          <span
            v-else
            class="text-neutral-400"
          >
            (Add a drawing in the project's Drawings register first, then attach
            a page here.)
          </span>
        </p>
      </section>

      <!-- Audit trail (from the immutable event log) -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <h2 class="mb-3 font-semibold">
          History
        </h2>
        <ol
          v-if="history && history.length"
          class="grid gap-2 text-sm"
        >
          <li
            v-for="h in history"
            :key="h.seq"
            class="flex flex-wrap items-baseline gap-x-2"
          >
            <span class="font-medium text-neutral-900">{{ h.label }}</span>
            <span class="text-neutral-500">{{ h.actor_name ?? "—" }}</span>
            <span class="text-neutral-400">· {{ fmtDateTime(h.at) }}</span>
          </li>
        </ol>
        <p
          v-else
          class="text-sm text-neutral-500"
        >
          No history yet.
        </p>
      </section>
    </div>
  </PageLayout>
</template>
